import { useCallback, useEffect, useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../outbox/db'
import type { SettingsRecord } from '../outbox/types'

export type SettingKey = SettingsRecord['key']

export interface DeviceSettings {
  sound: boolean
  haptics: boolean
  torch: boolean
}

export const DEFAULT_SETTINGS: DeviceSettings = {
  sound: true,
  haptics: true,
  torch: false,
}

export function useDeviceSettings(): {
  settings: DeviceSettings
  setSetting: (key: SettingKey, enabled: boolean) => Promise<void>
} {
  const records = useLiveQuery(() => db.settings.toArray(), [], [])
  const settings = useMemo(() => settingsFromRecords(records), [records])

  useEffect(() => {
    void ensureDefaultSettings()
  }, [])

  const setSetting = useCallback(async (key: SettingKey, enabled: boolean) => {
    await db.settings.put({ key, enabled })
  }, [])

  return { settings, setSetting }
}

export async function readDeviceSettings(): Promise<DeviceSettings> {
  const records = await db.settings.bulkGet(['sound', 'haptics', 'torch'])
  return settingsFromRecords(records)
}

async function ensureDefaultSettings(): Promise<void> {
  const records = await db.settings.bulkGet(['sound', 'haptics', 'torch'])
  const missing = (Object.keys(DEFAULT_SETTINGS) as SettingKey[])
    .flatMap((key, index) =>
      records[index] === undefined ? [{ key, enabled: DEFAULT_SETTINGS[key] }] : [],
    )

  if (missing.length > 0) {
    await db.settings.bulkPut(missing)
  }
}

function settingsFromRecords(records: Array<SettingsRecord | undefined>): DeviceSettings {
  const values = { ...DEFAULT_SETTINGS }

  for (const record of records) {
    if (record !== undefined) {
      values[record.key] = record.enabled
    }
  }

  return values
}
